// src/hooks/usePageView.ts
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../types";

const DEBOUNCE_MS = 30_000;

export function sendPageView(path: string, token?: string | null) {
  const body = JSON.stringify({ path, referrer: document.referrer || "" });
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // Raw fetch required for keepalive: true (beacon-style request not supported by Axios)
  fetch("/api/billing/pageview/", {
    method: "POST",
    headers,
    body,
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fire-and-forget page-view tracker for landing pages.
 * Sends JWT when available so the backend can mark authenticated views.
 * Rate-limited client-side: max 1 call per `path` per 30 seconds.
 */
const usePageView = (path: string) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const sentRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const now = Date.now();
    const last = sentRef.current[path] || 0;
    if (now - last < DEBOUNCE_MS) return;
    sentRef.current[path] = now;

    sendPageView(path, token);
  }, [path, token]);
};

export default usePageView;
